import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const CartContext = createContext(null);
const GUEST_CART_KEY = "nc_guest_cart";

function loadGuestCart() {
  try {
    const raw = localStorage.getItem(GUEST_CART_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function saveGuestCart(items) {
  try { localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items || [])); }
  catch { /* quota exceeded or disabled */ }
}

function clearGuestCart() {
  try { localStorage.removeItem(GUEST_CART_KEY); } catch { /* noop */ }
}

export function CartProvider({ children }) {
  const { isAuthenticated, isCustomer, sessionChecked } = useAuth();
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hasMergedRef = useRef(false);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // ---------- Fetch / refresh cart (server or guest) ----------
  const fetchCart = useCallback(async () => {
    if (!sessionChecked) return; // wait for auth status to settle
    try {
      setLoading(true);
      if (isAuthenticated && isCustomer) {
        const res = await api.get("/cart");
        setCart(res.data);
      } else {
        const raw = loadGuestCart();
        if (raw.length === 0) {
          setCart({ items: [], has_stock_issues: false });
        } else {
          const res = await api.post("/cart/preview", { items: raw });
          setCart({ items: res.data.items || [], has_stock_issues: !!res.data.has_stock_issues });
        }
      }
    } catch {
      setCart({ items: [], has_stock_issues: false });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, isCustomer, sessionChecked]);

  // ---------- Auto-merge guest cart into server cart on sign-in ----------
  useEffect(() => {
    if (!sessionChecked) return;
    (async () => {
      if (isAuthenticated && isCustomer && !hasMergedRef.current) {
        hasMergedRef.current = true;
        const guestItems = loadGuestCart();
        if (guestItems.length > 0) {
          try {
            const res = await api.post("/cart/merge", { items: guestItems });
            const merged = res.data?.merged || 0;
            const skipped = res.data?.skipped || 0;
            if (merged > 0 && skipped === 0) toast.success(`${merged} cart item${merged > 1 ? "s" : ""} restored`);
            else if (merged > 0 && skipped > 0) toast.success(`${merged} item${merged > 1 ? "s" : ""} restored · ${skipped} unavailable`);
            else if (merged === 0 && skipped > 0) toast.error(`${skipped} cart item${skipped > 1 ? "s" : ""} no longer available`);
          } catch { /* silent — we'll still fetch server cart below */ }
          clearGuestCart();
        }
      }
      if (!isAuthenticated) {
        hasMergedRef.current = false;
      }
      await fetchCart();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isCustomer, sessionChecked]);

  // ---------- Mutations ----------
  const addItem = useCallback(async (productId, variantId, qty = 1, opts = {}) => {
    const { offerId = null, offerTierQty = null } = opts;
    if (isAuthenticated && isCustomer) {
      await api.post("/cart/items", {
        product_id: productId,
        variant_id: variantId,
        quantity: qty,
        offer_id: offerId,
        offer_tier_qty: offerTierQty,
      });
      await fetchCart();
      return;
    }
    // Guest: mutate localStorage
    const items = loadGuestCart();
    const existing = items.find(i => i.variant_id === variantId);
    if (existing) {
      if (offerId) {
        // Offer adds replace the line (bundle semantics)
        existing.quantity = qty;
        existing.offer_id = offerId;
        existing.offer_tier_qty = offerTierQty;
      } else {
        existing.quantity += qty;
      }
    } else {
      items.push({
        product_id: productId,
        variant_id: variantId,
        quantity: qty,
        offer_id: offerId,
        offer_tier_qty: offerTierQty,
      });
    }
    saveGuestCart(items);
    await fetchCart();
  }, [isAuthenticated, isCustomer, fetchCart]);

  const updateItem = useCallback(async (variantId, qty) => {
    if (isAuthenticated && isCustomer) {
      await api.put(`/cart/items/${variantId}`, { quantity: qty });
      await fetchCart();
      return;
    }
    let items = loadGuestCart();
    if (qty <= 0) {
      items = items.filter(i => i.variant_id !== variantId);
    } else {
      const existing = items.find(i => i.variant_id === variantId);
      if (existing) existing.quantity = qty;
    }
    saveGuestCart(items);
    await fetchCart();
  }, [isAuthenticated, isCustomer, fetchCart]);

  const removeItem = useCallback(async (variantId) => {
    if (isAuthenticated && isCustomer) {
      await api.delete(`/cart/items/${variantId}`);
      await fetchCart();
      return;
    }
    const items = loadGuestCart().filter(i => i.variant_id !== variantId);
    saveGuestCart(items);
    await fetchCart();
  }, [isAuthenticated, isCustomer, fetchCart]);

  const clearCart = useCallback(async () => {
    if (isAuthenticated && isCustomer) {
      await api.delete("/cart");
      setCart({ items: [] });
      return;
    }
    clearGuestCart();
    setCart({ items: [] });
  }, [isAuthenticated, isCustomer]);

  const itemCount = cart.items?.reduce((sum, i) => sum + (i.quantity || 0), 0) || 0;

  return (
    <CartContext.Provider value={{ cart, loading, itemCount, fetchCart, addItem, updateItem, removeItem, clearCart, drawerOpen, openDrawer, closeDrawer }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
